import type { ReactNode } from 'react';

/**
 * THE JUMP, MOVED OFF THE VALUE (audit P1-8, owner's decision: the Attio pattern).
 *
 * In a table cell the value is not the link any more - a small icon at the cell's trailing
 * edge is - so the CELL is what a click lands on, which is what its own doc comment always
 * claimed: "the CELL is the affordance ... a table you can only read is half a CRM."
 *
 * The icon shows on hover or keyboard focus on a pointer device and is always there on a
 * touch screen, at the touch floor: `.cell-affordance` in index.css is where that split
 * lives, because "hover is never the only affordance" (DESIGN.md) and a finger has none.
 * It keeps its layout space in both states, so the value does not reflow when a pointer
 * arrives.
 */
export const CELL_AFFORDANCE =
  'cell-affordance shrink-0 rounded-control p-1 text-ink-subtle transition-colors hover:text-accent';

/**
 * A value that truncates, with its jump pinned to the trailing edge. The pair is one
 * component because the truncation and the icon are one decision: the icon must never be
 * what gets cut off.
 */
export function CellValueWithAffordance({
  children,
  affordance,
}: {
  readonly children: ReactNode;
  readonly affordance: ReactNode;
}) {
  return (
    <span className="flex min-w-0 items-center gap-1">
      <span className="min-w-0 flex-1 truncate">{children}</span>
      {affordance}
    </span>
  );
}

/**
 * A click on a link is a JUMP, never anything the surface around it does - an edit in a
 * table cell, a navigation on a card row. Shared by both shapes of every link display, so
 * the rule is written once; what P1-8 changed is not the rule but WHAT IT GUARDS, which in
 * a cell is now ~22 drawn pixels rather than 89% of the row's width.
 */
export function stopCellClick(event: { stopPropagation: () => void }): void {
  event.stopPropagation();
}
